import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Copy, Send, CheckCircle2, Save, Loader2, XCircle, ThumbsUp, MessageCircleReply } from "lucide-react";

const MAX = 320;
const chars = (t) => [...(t || "")].length;
const ago = (iso) => {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

export const SmsStatusBadge = ({ it, tid }) => {
  const p = { "data-testid": `sms-status-${tid}` };
  if (it.reply_yes) return <Badge className="bg-emerald-600 text-white" {...p}><ThumbsUp className="w-3 h-3 mr-1" />Replied YES — set it up in Gingr</Badge>;
  if (it.reply_at) return <Badge className="bg-rose-100 text-rose-700 border-rose-200" {...p}><MessageCircleReply className="w-3 h-3 mr-1" />Customer replied</Badge>;
  if (it.send_status === "sending") return <Badge className="bg-cyan-100 text-cyan-800" {...p}><Loader2 className="w-3 h-3 mr-1 animate-spin" />Sending via Gingr…</Badge>;
  if (it.send_status === "failed") return <Badge className="bg-rose-100 text-rose-700" {...p}><XCircle className="w-3 h-3 mr-1" />Send failed</Badge>;
  if (it.texted_at) return <Badge className="bg-slate-100 text-slate-700" {...p}><CheckCircle2 className="w-3 h-3 mr-1" />Texted {ago(it.texted_at)}{it.sent_via === "gingr" ? " via Gingr" : ""}</Badge>;
  return null;
};

// Editable ≤320-char SMS draft with Save / Copy / Mark as texted / Send via Gingr.
export const SmsDraftBox = ({ api, it, tid, name, onChanged, onText }) => {
  const [text, setText] = useState(it.draft || "");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setText(it.draft || ""); }, [it.draft]);
  useEffect(() => { onText?.(it.key, text); }, [text, it.key, onText]);
  const n = chars(text);
  const over = n > MAX;
  const dirty = text.trim() !== (it.draft || "").trim();
  const call = async (k, fn, ok) => {
    setBusy(k);
    try { await fn(); if (ok) toast.success(ok); await onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid={`sms-box-${tid}`}>
      {it.reply_text && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-sm text-emerald-900" data-testid={`sms-reply-${tid}`}>
          <MessageCircleReply className="inline w-4 h-4 mr-1" />“{it.reply_text}” <span className="text-xs opacity-70">· {ago(it.reply_at)}</span>
        </div>
      )}
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className={over ? "border-rose-400 focus-visible:ring-rose-400" : ""} data-testid={`sms-draft-${tid}`} />
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs font-semibold ${over ? "text-rose-600" : n > 300 ? "text-amber-600" : "text-gray-400"}`} data-testid={`sms-count-${tid}`}>{n}/{MAX}{over ? " — too long" : ""}</span>
        {it.draft_edited && <span className="text-[11px] text-gray-400">edited</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={!dirty || over || !text.trim() || !!busy} data-testid={`sms-save-${tid}`}
            onClick={() => call("save", () => axios.put(`${api}/draft`, { key: it.key, text: text.trim() }), "Draft saved")}>
            <Save className="w-3.5 h-3.5 mr-1" />Save
          </Button>
          <Button size="sm" variant="outline" disabled={over || !text.trim()} data-testid={`sms-copy-${tid}`}
            onClick={() => { navigator.clipboard?.writeText(text.trim()); toast.success("Copied"); }}>
            <Copy className="w-3.5 h-3.5 mr-1" />Copy
          </Button>
          <Button size="sm" variant="outline" disabled={!!busy} data-testid={`sms-mark-texted-${tid}`}
            onClick={() => call("mark", () => axios.post(`${api}/mark-texted`, { key: it.key }), "Marked as texted")}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Mark as texted
          </Button>
          <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-emerald-600" disabled={over || !text.trim() || !!busy || it.send_status === "sending"}
            onClick={() => setConfirm(true)} data-testid={`sms-send-${tid}`}>
            <Send className="w-3.5 h-3.5 mr-1" />Send via Gingr
          </Button>
        </div>
      </div>
      {it.send_status === "failed" && it.send_error && <p className="text-xs text-rose-600">{it.send_error}</p>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid={`sms-confirm-${tid}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Text {name || "this customer"} via Gingr?</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-wrap">{text.trim()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`sms-confirm-cancel-${tid}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`sms-confirm-send-${tid}`}
              onClick={() => call("send", () => axios.post(`${api}/send`, { key: it.key, text: text.trim() }), "Sending via Gingr — about 30 seconds")}>
              Send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
